import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Twitter, Linkedin, Youtube } from 'lucide-react';
import { useLanguage, useMarketplaceMode, useTheme } from '../App';
import { useCompanyInfo } from '../hooks/useCompanyInfo';
import PaymentMethodsStrip from './PaymentMethodsStrip';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useLanguage();
  const tr = typeof t === 'function' ? t : ((key) => key);
  const { isMultiVendor } = useMarketplaceMode() || {};
  const { theme } = useTheme();
  const { data: co } = useCompanyInfo();
  const social = co?.social || {};

  return (
    <footer className="bg-foreground text-background" data-testid="footer">
      <div className="container mx-auto px-4 py-12 md:py-16">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="inline-flex items-center mb-4">
              <picture>
                <source
                  type="image/webp"
                  srcSet={theme === 'dark' ? '/logo.webp' : '/logo-dark.webp'}
                />
                <img
                  src={theme === 'dark' ? '/logo.png' : '/logo-dark.png'}
                  alt="ZIVU — Parts Delivered"
                  width="80"
                  height="40"
                  className="h-10 object-contain"
                />
              </picture>
            </Link>
            <p className="text-sm text-background/70 leading-relaxed mb-4">
              {tr('footer.tagline')}
            </p>
            {/* Social Media — only show icons where URL is configured */}
            <div className="flex items-center gap-3">
              {/* Facebook + Instagram intentionally hidden until accounts exist */}
              {social.twitter && (
                <a
                  href={social.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-accent transition-colors"
                  aria-label="Twitter / X"
                >
                  <Twitter className="h-5 w-5" />
                </a>
              )}
              {social.linkedin && (
                <a
                  href={social.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-accent transition-colors"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="h-5 w-5" />
                </a>
              )}
              {social.youtube && (
                <a
                  href={social.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-accent transition-colors"
                  aria-label="YouTube"
                >
                  <Youtube className="h-5 w-5" />
                </a>
              )}
            </div>
          </div>

          {/* Quick Links — Shop categories
              Hidden 2026-04-30 per request. Restore by removing the
              `false &&` guard once we curate the category list properly. */}
          {false && (
          <div>
            <h4 className="font-heading font-bold uppercase tracking-wide text-sm mb-4">{tr('footer.shop')}</h4>
            <ul className="space-y-2 text-sm text-background/70">
              <li><Link to="/search" className="hover:text-accent transition-colors">{tr('footer.allParts')}</Link></li>
              <li><Link to="/category/cat_engine?category=cat_engine" className="hover:text-accent transition-colors">{tr('footer.engineParts')}</Link></li>
              <li><Link to="/category/cat_body-body-parts-hook?category=cat_body-body-parts-hook" className="hover:text-accent transition-colors">{tr('footer.bodyParts')}</Link></li>
              <li><Link to="/category/cat_devices-switches-electronic-system?category=cat_devices-switches-electronic-system" className="hover:text-accent transition-colors">{tr('footer.electrical')}</Link></li>
              <li><Link to="/search?condition=refurbished" className="hover:text-accent transition-colors">{tr('footer.refurbished')}</Link></li>
            </ul>
          </div>
          )}

          {/* Information */}
          <div>
            <h4 className="font-heading font-bold uppercase tracking-wide text-sm mb-4">{tr('footer.information')}</h4>
            <ul className="space-y-2 text-sm text-background/70">
              <li><Link to="/about" className="hover:text-accent transition-colors">{tr('footer.aboutUs')}</Link></li>
              <li><Link to="/delivery" className="hover:text-accent transition-colors">{tr('footer.deliveryInfo')}</Link></li>
              <li><Link to="/returns" className="hover:text-accent transition-colors">{tr('footer.returnsRefunds')}</Link></li>
              <li><Link to="/payment" className="hover:text-accent transition-colors">{tr('footer.paymentMethods')}</Link></li>
              <li><Link to="/contact" className="hover:text-accent transition-colors">{tr('footer.contactUs')}</Link></li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="font-heading font-bold uppercase tracking-wide text-sm mb-4">{tr('footer.support')}</h4>
            <ul className="space-y-2 text-sm text-background/70">
              <li><Link to="/help" className="hover:text-accent transition-colors">{tr('footer.helpCenter')}</Link></li>
              <li><Link to="/shipping" className="hover:text-accent transition-colors">{tr('footer.shippingInfo')}</Link></li>
              <li><Link to="/faq" className="hover:text-accent transition-colors">{tr('footer.faq')}</Link></li>
              <li><Link to="/warranty" className="hover:text-accent transition-colors">{tr('footer.warranty')}</Link></li>
            </ul>
          </div>

          {/* Sellers — hidden in single-vendor mode */}
          {isMultiVendor && (
          <div>
            <h4 className="font-heading font-bold uppercase tracking-wide text-sm mb-4">{tr('footer.sellers')}</h4>
            <ul className="space-y-2 text-sm text-background/70">
              <li><Link to="/register" className="hover:text-accent transition-colors">{tr('footer.sellOnZivu')}</Link></li>
              <li><Link to="/seller" className="hover:text-accent transition-colors">{tr('footer.sellerPortal')}</Link></li>
              <li><Link to="/seller/terms" className="hover:text-accent transition-colors">{tr('footer.sellerTerms')}</Link></li>
            </ul>
          </div>
          )}
        </div>

        {/* Payment Providers */}
        <div className="mt-12 pt-8 border-t border-background/20">
          <div className="mb-6">
            <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-background/70 text-center mb-4">
              Accepted Payment Methods
            </p>
            <PaymentMethodsStrip />
            <p className="text-center mt-3">
              <Link to="/payment" className="text-xs text-background/70 underline underline-offset-2 hover:text-accent transition-colors">
                Payment methods &amp; security
              </Link>
            </p>
          </div>
        </div>

        {/* Legal identity — required for Merchant Center business transparency.
            Sourced from platform_settings.company_info with hardcoded fallbacks so the
            details render even if the API is slow or unavailable. */}
        <div className="pt-6 pb-6 border-t border-background/20">
          <div className="text-xs text-background/70 leading-relaxed space-y-1">
            <p className="font-semibold text-background/90">
              {co?.company_name || 'SIA Cono Group'}
              {co?.legal_form ? '' : ''} &middot; trading as ZIVU
            </p>
            <p>
              {co?.address_line1 || 'Caunes iela 10 k-2 - 2'}, {co?.postal_code || 'LV-1006'} {co?.city || 'R\u012bga'}, {co?.country_name || 'Latvia'}
            </p>
            <p>
              Reg. No. {co?.registry_code || '45403026746'} &middot; VAT {co?.vat_id || 'LV45403026746'}
            </p>
            <p>
              <a href={`tel:${(co?.phone || '+37120004685').replace(/\s/g, '')}`} className="hover:text-accent transition-colors">
                {co?.phone || '+371 2000 4685'}
              </a>
              {' '}&middot;{' '}
              <a href={`mailto:${co?.email || 'support@zivu.eu'}`} className="hover:text-accent transition-colors">
                {co?.email || 'support@zivu.eu'}
              </a>
              {' '}&middot; Mon&ndash;Fri, 9:00&ndash;18:00 EET
            </p>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-6 border-t border-background/20">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-background/70">
            <div className="flex items-center gap-4 flex-wrap">
              <span>&copy; {currentYear} ZIVU</span>
              <span className="hidden md:inline">&bull;</span>
              <Link to="/privacy" className="hover:text-background transition-colors">{tr('footer.privacy')}</Link>
              <Link to="/terms" className="hover:text-background transition-colors">{tr('footer.terms')}</Link>
              <Link to="/imprint" className="hover:text-background transition-colors">Imprint</Link>
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent('zivu:openCookiePreferences'))}
                className="hover:text-background transition-colors underline-offset-2 hover:underline"
                aria-label="Reopen cookie preferences"
              >
                Cookie Preferences
              </button>
            </div>
            <div className="flex items-center gap-4">
              <span className="font-mono text-xs uppercase tracking-wide">{tr('footer.euMarketplace')}</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
